import React, { useState, useCallback, useEffect } from 'react';

interface SessionMetadata {
    [key: string]: unknown;
}

interface Session {
    id: string;
    startTime: string;
    lessonsAccessed: string[];
    endTime?: string;
    lessonCount?: number;
    [key: string]: unknown;
}

interface LessonAccessData {
    lessonId: string;
    lessonsAccessed: string[];
    lessonCount: number;
}

interface UseSessionDataOptions {
    onSessionCreated?: ((session: Session) => void) | null;
    onLessonAccessed?: ((data: LessonAccessData) => void) | null;
    onSessionClosed?: ((session: Session) => void) | null;
}

interface UseSessionDataResult {
    currentSessionId: string | null;
    lessonsAccessed: string[];
    sessionHistory: Session[];
    currentSession: Session | null;
    lessonCount: number;
    sessionCount: number;
    createSession: (sessionId: string, metadata?: SessionMetadata) => void;
    recordLessonAccess: (lessonId: string) => void;
    closeSession: () => void;
}

export const useSessionData = (options: UseSessionDataOptions = {}): UseSessionDataResult => {
    const {
        onSessionCreated = null,
        onLessonAccessed = null,
        onSessionClosed = null
    } = options;

    const [currentSessionId, setCurrentSessionId] = useState<string | null>(() => {
        if (typeof window !== 'undefined' && (window as any).MOCK_SESSION_ID) {
            // console.log('[useSessionData] Initializing with MOCK_SESSION_ID:', (window as any).MOCK_SESSION_ID);
            return (window as any).MOCK_SESSION_ID;
        }
        return null;
    });
    const [lessonsAccessed, setLessonsAccessed] = useState<string[]>([]);
    const [sessionHistory, setSessionHistory] = useState<Session[]>([]);
    const [currentSession, setCurrentSession] = useState<Session | null>(() => {
        if (typeof window !== 'undefined' && (window as any).MOCK_SESSION_ID) {
            return {
                id: (window as any).MOCK_SESSION_ID,
                startTime: new Date().toISOString(),
                lessonsAccessed: []
            };
        }
        return null;
    });

    const lessonCount = lessonsAccessed.length;
    const sessionCount = sessionHistory.length;

    const createSession = useCallback((sessionId: string, metadata: SessionMetadata = {}): void => {
        // console.log('[useSessionData] createSession called', { sessionId });
        const now = new Date().toISOString();
        const session: Session = {
            id: sessionId,
            startTime: now,
            lessonsAccessed: [],
            ...metadata
        };

        setCurrentSessionId(sessionId);
        setCurrentSession(session);
        setLessonsAccessed([]);

        if (onSessionCreated) {
            onSessionCreated(session);
        }
    }, [onSessionCreated]);

    const recordLessonAccess = useCallback((lessonId: string): void => {
        if (!lessonId) return;

        setLessonsAccessed(prev => {
            if (prev.includes(lessonId)) return prev;

            const updatedLessons = [...prev, lessonId];

            // Note: We're calling the callback inside the functional update if we need the most recent state
            // or we could use another effect to trigger the callback when lessonsAccessed changes.
            // For now, let's keep it simple and stable.
            return updatedLessons;
        });
    }, []);

    // Trigger callback when lessonsAccessed changes
    useEffect(() => {
        if (lessonsAccessed.length > 0 && onLessonAccessed) {
            const lastLessonId = lessonsAccessed[lessonsAccessed.length - 1];
            onLessonAccessed({
                lessonId: lastLessonId,
                lessonsAccessed,
                lessonCount: lessonsAccessed.length
            });
        }
    }, [lessonsAccessed, onLessonAccessed]);

    const closeSession = useCallback((): void => {
        // console.log('[useSessionData] closeSession called', { currentSessionId });
        // console.log('[useSessionData] closeSession stack trace:', new Error().stack);
        if (currentSessionId && currentSession) {
            const now = new Date().toISOString();
            const closedSession: Session = {
                ...currentSession,
                endTime: now,
                lessonsAccessed: lessonsAccessed,
                lessonCount: lessonsAccessed.length
            };

            setSessionHistory(prev => [...prev, closedSession]);

            if (onSessionClosed) {
                onSessionClosed(closedSession);
            }
        }

        setCurrentSessionId(null);
        setCurrentSession(null);
        setLessonsAccessed([]);
    }, [currentSessionId, currentSession, lessonsAccessed, onSessionClosed]);

    return React.useMemo(() => ({
        currentSessionId,
        lessonsAccessed,
        sessionHistory,
        currentSession,
        lessonCount,
        sessionCount,
        createSession,
        recordLessonAccess,
        closeSession
    }), [
        currentSessionId,
        lessonsAccessed,
        sessionHistory,
        currentSession,
        lessonCount,
        sessionCount,
        createSession,
        recordLessonAccess,
        closeSession
    ]);
};

export default useSessionData;
